import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  SelectField,
  StatusPill,
  buttonClasses,
  cn,
  type StatusTone,
} from '@homecare/ui';
import { Link } from '@tanstack/react-router';
import { Upload } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

/**
 * The roster (§7).
 *
 * Avatar, name, client number, status pill — the mockup's treatment, built from
 * the kit. Search by name or number, filter by status and payer, sortable,
 * virtualised past 200 rows.
 *
 * ## Why it accumulates pages rather than paging
 *
 * The API caps `pageSize` at 100. A pager would mean virtualisation never
 * engages — 100 rows fit on any screen — which would make "virtualised past 200
 * rows" a feature nothing exercises.
 *
 * More importantly it is the wrong shape for the job. Somebody looking for a
 * client scrolls and scans; they do not know which page of 100 the person is
 * on, and paging turns one search into a hunt. So the list loads a page at a
 * time into one continuous roster, and past `VIRTUALISE_ABOVE` rows it renders
 * only what is on screen.
 */

const PAGE_SIZE = 100;

/**
 * Where windowing starts.
 *
 * Below this the whole list is in the DOM, which keeps find-in-page working and
 * costs nothing. Above it the browser starts to feel a 200-row table with an
 * avatar and a pill in every row, so only the visible slice is rendered.
 */
export const VIRTUALISE_ABOVE = 200;

/** Rendered row height, and the overscan either side of the viewport. */
const ROW_HEIGHT = 64;
const OVERSCAN = 8;

interface PayerRef {
  id: string;
  name: string;
}

interface ClientRow {
  id: string;
  mrn: string;
  firstName: string;
  lastName: string;
  preferredName?: string | null;
  status: StatusTone extends never ? never : string;
  /** Who pays, in the order minutes are absorbed. Empty is a real state. */
  payers: PayerRef[];
}

interface ClientPage {
  clients: ClientRow[];
  page: number;
  pageSize: number;
  total: number;
}

const STATUS_TONE: Record<string, StatusTone> = {
  REFERRAL: 'scheduled',
  INTAKE: 'progress',
  ACTIVE: 'verified',
  ON_HOLD: 'warning',
  DISCHARGED: 'inactive',
};

const STATUS_LABEL: Record<string, string> = {
  REFERRAL: 'Referral',
  INTAKE: 'Intake',
  ACTIVE: 'Active',
  ON_HOLD: 'On hold',
  DISCHARGED: 'Discharged',
};

type SortKey = 'name' | 'mrn' | 'status';

export function ClientList(): ReactNode {
  const api = useApi();
  const canCreate = useHasPermission('client:create');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [payer, setPayer] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [ascending, setAscending] = useState(true);
  const [pages, setPages] = useState(1);

  const query = useQuery({
    queryKey: ['clients', 'list', search, status, payer, pages],
    queryFn: async () => {
      const collected: ClientRow[] = [];
      let total = 0;

      // One request per page held, so "load more" is a refetch of the whole
      // set rather than a cache the list has to reconcile by hand.
      for (let page = 1; page <= pages; page += 1) {
        const parameters = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
        if (search.trim()) parameters.set('search', search.trim());
        if (status) parameters.set('status', status);
        if (payer) parameters.set('payerId', payer);

        const result = await api.get<ClientPage>(`/v1/clients?${parameters.toString()}`);
        collected.push(...result.clients);
        total = result.total;
      }

      return { clients: collected, total };
    },
  });

  /**
   * The filter's options are the tenant's payers, not the ones on screen.
   *
   * Deriving them from the loaded roster looked equivalent and was not. It
   * could only ever offer a payer some already-loaded client used, so a payer
   * an agency had configured but not yet assigned was unfilterable, and the
   * options changed as more of the roster loaded. Worse, it made the control
   * describe the data instead of the agency: with the list response carrying no
   * payer at all, the set was empty for every tenant and the field read
   * "Appears once clients have payers" against a database full of them.
   *
   * A separate query rather than a field on the list response: the answer is
   * per tenant, not per page, and this way it is fetched once and cached rather
   * than repeated in all 100 rows.
   */
  const payerOptions = useQuery({
    queryKey: ['payers', 'options'],
    queryFn: () => api.get<{ payers: PayerRef[] }>('/v1/payers'),
    staleTime: 5 * 60 * 1000,
  });

  const payers = useMemo(
    () => [...(payerOptions.data?.payers ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [payerOptions.data],
  );

  const rows = useMemo(() => {
    // Filtering is the server's, not this component's — the roster is paged,
    // and narrowing what happens to be loaded would answer "who pays" for the
    // first hundred clients and quietly leave out the rest.
    const filtered = query.data?.clients ?? [];

    const direction = ascending ? 1 : -1;

    return [...filtered].sort((left, right) => {
      if (sort === 'mrn') return left.mrn.localeCompare(right.mrn) * direction;
      if (sort === 'status') return left.status.localeCompare(right.status) * direction;

      return (
        `${left.lastName} ${left.firstName}`.localeCompare(`${right.lastName} ${right.firstName}`) *
        direction
      );
    });
  }, [query.data, sort, ascending]);

  const toggleSort = (key: SortKey): void => {
    if (sort === key) setAscending((was) => !was);
    else {
      setSort(key);
      setAscending(true);
    }
  };

  const loaded = query.data?.clients.length ?? 0;
  const total = query.data?.total ?? 0;

  return (
    <div className="grid gap-5">
      <PageHeader
        title="Clients"
        meta={
          query.isPending
            ? 'Loading the roster…'
            : `${total} on the roster${rows.length === total ? '' : ` · ${rows.length} shown`}`
        }
        action={
          /*
           * Two ways onto the roster, and both need a door.
           *
           * `/clients/import` and the whole of §5 behind it existed, was
           * routed, and was tested — and had no link anywhere in the console,
           * so the only way to reach it was to type the URL. An end-to-end test
           * that navigates with `page.goto` passes against exactly that, which
           * is how it went unnoticed: the feature worked and was unreachable.
           *
           * Import sits beside "Add a client" rather than inside a menu because
           * the two are the same decision made once — a new agency arrives with
           * a roster, and loading it is the first thing they do (R-1d).
           *
           * Both are gated on `client:create`, the permission their routes
           * already require. An action that leads to "no access" is a worse
           * answer than no action at all, and §12 asks for hidden rather than
           * disabled for the same reason.
           */
          canCreate ? (
            <div className="flex items-center gap-2">
              <Link
                to="/clients/import"
                className={buttonClasses({ variant: 'secondary' })}
                data-testid="import-clients"
              >
                <Upload aria-hidden="true" className="size-4" />
                Import a roster
              </Link>
              <Link to="/clients/new" className={buttonClasses()}>
                Add a client
              </Link>
            </div>
          ) : null
        }
      />

      <Card>
        <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1fr]">
          <Field
            label="Search"
            placeholder="Name or client number"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPages(1);
            }}
          />

          <SelectField
            label="Status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPages(1);
            }}
          >
            <option value="">Any status</option>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Payer"
            value={payer}
            onChange={(event) => {
              setPayer(event.target.value);
              setPages(1);
            }}
            hint={
              payerOptions.isPending || payers.length > 0
                ? undefined
                : 'Appears once your agency has payers'
            }
          >
            <option value="">Any payer</option>
            {payers.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </SelectField>
        </div>
      </Card>

      <ErrorNote error={query.error} nextStep="Reload the page, or narrow the search." />

      {query.isPending ? (
        <Card>
          <p className="text-ink-3 py-8 text-center text-sm">Loading the roster…</p>
        </Card>
      ) : rows.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            title={search || status || payer ? 'Nobody matches that' : 'No clients yet'}
            explanation={
              search || status || payer
                ? 'Try a different name or client number, or clear the filters.'
                : 'Everyone your agency cares for lives here. Add the first one, or import your existing roster.'
            }
            action={
              search || status || payer ? undefined : (
                <Link to="/clients/import" className={buttonClasses()}>
                  Import a roster
                </Link>
              )
            }
          />
        </Card>
      ) : (
        <ClientTable
          rows={rows}
          sort={sort}
          ascending={ascending}
          onSort={toggleSort}
          loaded={loaded}
          total={total}
          onLoadMore={() => setPages((was) => was + 1)}
          loadingMore={query.isFetching}
        />
      )}
    </div>
  );
}

function ClientTable({
  rows,
  sort,
  ascending,
  onSort,
  loaded,
  total,
  onLoadMore,
  loadingMore,
}: {
  rows: ClientRow[];
  sort: SortKey;
  ascending: boolean;
  onSort: (key: SortKey) => void;
  loaded: number;
  total: number;
  onLoadMore: () => void;
  loadingMore: boolean;
}): ReactNode {
  const scroller = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(800);

  const virtualise = rows.length > VIRTUALISE_ABOVE;

  const first = virtualise ? Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN) : 0;
  const last = virtualise
    ? Math.min(rows.length, Math.ceil((scrollTop + viewport) / ROW_HEIGHT) + OVERSCAN)
    : rows.length;

  const visible = rows.slice(first, last);

  return (
    <Card padded={false}>
      <table className="w-full border-collapse">
        <caption className="sr-only">
          Clients on the roster, sorted by {sort === 'mrn' ? 'client number' : sort}.
        </caption>
        <thead>
          <tr className="border-line border-b">
            <SortableHeader
              label="Client"
              active={sort === 'name'}
              ascending={ascending}
              onClick={() => onSort('name')}
            />
            <SortableHeader
              label="Client #"
              active={sort === 'mrn'}
              ascending={ascending}
              onClick={() => onSort('mrn')}
            />
            <SortableHeader
              label="Payer"
              active={false}
              ascending={ascending}
              onClick={undefined}
            />
            <SortableHeader
              label="Status"
              active={sort === 'status'}
              ascending={ascending}
              onClick={() => onSort('status')}
            />
          </tr>
        </thead>
      </table>

      <div
        ref={scroller}
        // A scroll container only when windowing. Below the threshold the page
        // scrolls, which keeps find-in-page and keyboard paging working.
        className={cn(virtualise && 'max-h-[60vh] overflow-y-auto')}
        onScroll={
          virtualise
            ? (event) => {
                setScrollTop(event.currentTarget.scrollTop);
                setViewport(event.currentTarget.clientHeight);
              }
            : undefined
        }
      >
        <table className="w-full border-collapse">
          <tbody>
            {/* Spacers carry the scroll height of the rows that are not
                rendered, so the scrollbar reflects the whole roster rather than
                the window. */}
            {first > 0 && <tr style={{ height: first * ROW_HEIGHT }} aria-hidden="true" />}

            {visible.map((client) => (
              <tr
                key={client.id}
                className="border-line hover:bg-surface-2 duration-fast ease-calm border-b transition-colors last:border-b-0"
              >
                <td className="px-5 py-3">
                  <Link
                    to="/clients/$clientId"
                    params={{ clientId: client.id }}
                    className="flex items-center gap-3"
                  >
                    <Avatar name={`${client.firstName} ${client.lastName}`} seed={client.id} />
                    <span>
                      <span className="text-ink text-md block font-semibold">
                        {client.firstName} {client.lastName}
                      </span>
                      {client.preferredName && (
                        <span className="text-ink-3 block text-xs">
                          Goes by {client.preferredName}
                        </span>
                      )}
                    </span>
                  </Link>
                </td>
                <td className="text-ink-2 px-5 py-3 font-mono text-sm" data-numeric>
                  {client.mrn}
                </td>
                <td className="text-ink-2 px-5 py-3 text-sm">
                  <PayerCell payers={client.payers} />
                </td>
                <td className="px-5 py-3">
                  <StatusPill tone={STATUS_TONE[client.status] ?? 'inactive'}>
                    {STATUS_LABEL[client.status] ?? client.status}
                  </StatusPill>
                </td>
              </tr>
            ))}

            {last < rows.length && (
              <tr style={{ height: (rows.length - last) * ROW_HEIGHT }} aria-hidden="true" />
            )}
          </tbody>
        </table>
      </div>

      {loaded < total && (
        <div className="border-line border-t p-4 text-center">
          <Button variant="secondary" onClick={onLoadMore} pending={loadingMore}>
            Load the next {Math.min(PAGE_SIZE, total - loaded)}
          </Button>
          <p className="text-ink-3 mt-2 text-xs">
            {loaded} of {total} loaded
          </p>
        </div>
      )}
    </Card>
  );
}

function SortableHeader({
  label,
  active,
  ascending,
  onClick,
}: {
  label: string;
  active: boolean;
  ascending: boolean;
  onClick: (() => void) | undefined;
}): ReactNode {
  return (
    // `aria-sort` goes here rather than on the button. It is only permitted on
    // an element with a `columnheader` or `rowheader` role, and axe flags it on
    // anything else — which it did, on all three sortable columns.
    <th
      scope="col"
      className="eyebrow px-5 py-3 text-left"
      aria-sort={active ? (ascending ? 'ascending' : 'descending') : undefined}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="hover:text-ink duration-fast ease-calm inline-flex items-center gap-1 transition-colors"
        >
          {label}
          <span aria-hidden="true" className={cn(!active && 'opacity-0')}>
            {ascending ? '▲' : '▼'}
          </span>
        </button>
      ) : (
        label
      )}
    </th>
  );
}

/**
 * How a list of payers reads in one cell.
 *
 * The column answers "who pays for this person" while somebody scans it, so it
 * leads with the payer that absorbs the visit first — the same order
 * `GET /v1/clients/:id/coverage` uses — and says how many others there are
 * rather than truncating to one and looking identical to a single-payer client.
 *
 * That difference is the point of the column. Two payers means some visits are
 * authorized and capped and the rest are billed elsewhere, which is a different
 * scheduling decision from one payer covering everything; a cell that showed
 * only the first name would report the two cases as the same.
 *
 * Exported as a pure function so the reading can be asserted without a browser.
 */
export function payerLabel(payers: { name: string }[]): {
  primary: string | null;
  extra: string | null;
  full: string | null;
} {
  const [first, ...rest] = payers;

  if (!first) return { primary: null, extra: null, full: null };

  return {
    primary: first.name,
    extra: rest.length === 0 ? null : `+${rest.length}`,
    full: payers.map((payer) => payer.name).join(' · '),
  };
}

function PayerCell({ payers }: { payers: PayerRef[] }): ReactNode {
  const { primary, extra, full } = payerLabel(payers);

  // "No payer" rather than "Not set". §18.2 step 4 is a real state somebody has
  // to act on — nobody is paying for these visits — and "not set" reads like a
  // field one person forgot to fill in rather than a client nobody is funding.
  //
  // And not "no payer yet" either: coverage lapses, so this cell is reached by
  // a client whose Medicaid arrangement ended last month as well as by one who
  // never had one. "Yet" would tell the first of those the wrong story.
  if (!primary) return <span className="text-ink-3">No payer</span>;

  return (
    <span title={full ?? undefined}>
      {primary}
      {extra && (
        <span className="text-ink-3 ml-1.5" aria-label={`and ${extra.slice(1)} more`}>
          {extra}
        </span>
      )}
    </span>
  );
}
